import { useDebounceFn } from '@vueuse/core'
import { useNuxtApp, useRoute } from 'nuxt/app'

import { useAuth } from '@common/auth-layer/composables/useAuth'
import { getConfig } from '@common/core-layer/config'
import { isCoursesContextPath } from '@common/core-layer/utils/route-path-segments'

import type { Pinia } from 'pinia'

export default defineNuxtPlugin(async () => {
  const route = useRoute()
  let currentSource: 'courses' | 'communities' = 'communities'

  /**
   * Determine DRM source based on route path
   * - Top-level `/courses` app routes and group `/learning/courses/` paths → courses
   * - Otherwise → communities (including group slugs like `courses-MD`)
   */
  const getDrmSource = (path: string): 'courses' | 'communities' => {
    if (isCoursesContextPath(path)) {
      return 'courses'
    }
    return 'communities'
  }

  /**
   * Initialize or reinitialize Assets DRM client with the given source
   */
  const initializeDrmClient = async (source: 'courses' | 'communities') => {
    const config = getConfig()
    try {
      const { initAssetsDrmClient } = await import('@ghl-revex/assets-drm-client/setup')
      initAssetsDrmClient({
        // in local i.e development, point assets drm to staging else use config mode
        env: config?.mode === 'development' ? 'staging' : config?.mode || 'staging',
        source,
        getAuthTokenCallback: getAuthTokenCallback(),
        headers: {
          source: 'PORTAL_USER',
        },
      })
      currentSource = source
    } catch (error) {
      console.error('Failed to initialize assets drm client:', error)
    }
  }

  // Initialize with current route source
  const initialSource = getDrmSource(route.path)
  await initializeDrmClient(initialSource)

  // Debounced route watcher to handle source changes
  const handleRouteChange = useDebounceFn(async (newPath: string) => {
    const newSource = getDrmSource(newPath)
    if (newSource !== currentSource) {
      await initializeDrmClient(newSource)
    }
  }, 300) // 300ms debounce

  // Watch for route changes
  watch(() => route.path, handleRouteChange)
})

function getAuthTokenCallback() {
  return async () => {
    const pinia = useNuxtApp().$pinia as Pinia
    const { store } = useAuth(pinia)
    return store.token || ''
  }
}
